// AdminOffers — list page for the rewards / offers admin section.
// Card grid layout in the new aesthetic, light + dark.
//
// Mobile: 1 col stacked horizontal cards. Desktop: 2-3 col grid.
//
// Phase 4 UI overhaul (2026-05-15): mounted inside RewardsHub as the
// "Offers" sub-tab — the hub already provides the outer max-w, the
// "Rewards" page title, and the sub-tab strip. So this panel drops:
//   • its outer `max-w-screen-2xl` wrapper
//   • the stale "Back to More" link
//   • the duplicated "Offers & Rewards" h1 header
// What remains: an action row ("+ New offer" + result count), the
// search + filter row, error/empty states, and the offer card grid.
// Surface tokens are realigned with arena/neon + ring-1 ring-inset
// per the Phase 2/3 design system. All data + API calls unchanged.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type OfferRow = {
  id: number;
  title: string;
  description: string | null;
  /** Phase 14: artwork URL (null = no artwork uploaded yet). */
  image_url: string | null;
  offer_type: string;
  reward_kind: 'cash'|'percent'|'item';
  reward_value: number;
  reward_cap: number | null;
  approval: 'auto'|'manual';
  starts_at: string | null;
  ends_at: string | null;
  per_user_limit: number;
  global_budget: number | null;
  budget_used: number;
  status: 'draft'|'active'|'paused'|'archived';
  created_at: string;
};

const STATUS_PILL: Record<string, string> = {
  draft:    'bg-arena-100/60 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-700/50 dark:text-neon-200/70 dark:ring-arena-600/40',
  active:   'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
  paused:   'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
  archived: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/30',
};

const TYPE_LABEL: Record<string, string> = {
  recharge_bonus:     'Recharge Bonus',
  signup_bonus:       'Signup Bonus',
  daily_checkin:      'Daily Check-in',
  play_streak:        'Play Streak',
  game_challenge:     'Game Challenge',
  win_streak:         'Win Streak',
  time_played:        'Time Played',
  wallet_spend:       'Wallet Spend',
  cashback:           'Cashback',
  admin_announcement: 'Announcement',
};

const PAGE_SIZE = 24;
const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function OffersListPanel() {
  const [rows, setRows] = useState<OfferRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType]     = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (debounced) params.set('search', debounced);
      if (status) params.set('status', status);
      if (type)   params.set('offer_type', type);
      const r = await adminApi.get<{ rows: OfferRow[]; total: number }>(`/admin/offers?${params}`);
      setRows(r.rows); setTotal(r.total);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }, [debounced, status, type, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debounced, status, type]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    // Phase 4 (2026-05-15): no outer max-w wrapper — RewardsHub owns it.
    <div className="space-y-4">
      {/* Action row: count summary + "New offer" CTA. Replaces the old
          page-level h1 header which competed with the RewardsHub title. */}
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12.5px] text-arena-600 dark:text-neon-200/70">
          {loading && rows.length === 0
            ? 'Loading…'
            : total === 0
              ? 'No offers yet'
              : <>Showing <b className="text-arena-900 dark:text-white tabular-nums">{rows.length}</b> of <b className="text-arena-900 dark:text-white tabular-nums">{total}</b> offer{total === 1 ? '' : 's'}</>}
        </div>
        <Link to="/offers/new"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full
                         bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                         text-white text-[13px] font-bold
                         shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                         transition active:scale-95">
          <Icon name="add" size={16} /> New offer
        </Link>
      </motion.div>

      {/* Search + filters — sticky inside panel so they stay reachable
          while the grid scrolls. backdrop-blur layer matches the
          pattern used by every Phase-2 panel's filter strip. */}
      <div className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                      backdrop-blur-md
                      bg-white/70 dark:bg-arena-900/60
                      flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 min-w-0">
          <Icon name="search" size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-500 dark:text-neon-200/55 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or description…"
            className="w-full rounded-full pl-9 pr-3 py-2 text-[13px]
                       bg-white dark:bg-arena-800/60
                       ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                       text-arena-900 dark:text-white
                       placeholder-arena-400 dark:placeholder-neon-200/40
                       focus:outline-none focus:ring-2 focus:ring-violet-400/60"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="rounded-full px-3 py-2 text-[13px]
                           bg-white dark:bg-arena-800/60
                           ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                           text-arena-900 dark:text-white
                           focus:outline-none focus:ring-2 focus:ring-violet-400/60">
          <option value="">Any status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}
                className="rounded-full px-3 py-2 text-[13px]
                           bg-white dark:bg-arena-800/60
                           ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                           text-arena-900 dark:text-white
                           focus:outline-none focus:ring-2 focus:ring-violet-400/60">
          <option value="">Any type</option>
          {Object.entries(TYPE_LABEL).map(([k, label]) =>
            <option key={k} value={k}>{label}</option>
          )}
        </select>
      </div>

      {err && (
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10
                        ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                        text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40
                        ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                        shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                        backdrop-blur p-10 text-center">
          <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl
                          bg-arena-100/60 dark:bg-arena-800/60 mb-3">
            <Icon name="card_giftcard" size={26} className="text-arena-400 dark:text-neon-200/40" />
          </div>
          <p className="text-[13px] text-arena-800 dark:text-white font-bold">No offers match</p>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-0.5">
            Try a different search or filter, or click "+ New offer".
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((o, i) => <OfferCard key={o.id} o={o} index={i} />)}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 pt-1 text-[12px] text-arena-600 dark:text-neon-200/60">
          <button disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/80
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums px-1">Page {page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/80
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Offer card ─────────────────────────────────────────────────────

function OfferCard({ o, index }: { o: OfferRow; index: number }) {
  const reward = o.reward_kind === 'percent'
    ? `${o.reward_value}%${o.reward_cap ? ` (cap ${inr(o.reward_cap)})` : ''}`
    : inr(o.reward_value);
  const budgetText = o.global_budget == null
    ? null
    : `${inr(o.budget_used)} / ${inr(o.global_budget)}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.02, 0.2) }}
    >
      {/* 2026-05-16 (Routing Restructure): canonical detail URL is
          /offers/:id (was /rewards/offers/:id). */}
      <Link to={`/offers/${o.id}`}
            className="block rounded-2xl p-4
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                       backdrop-blur
                       hover:-translate-y-0.5 hover:ring-violet-300/70 dark:hover:ring-violet-400/40 transition
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60">
        <div className="flex items-start gap-3 mb-3">
          {/* Phase 14: thumbnail variant when artwork present, else
              fall back to the card_giftcard icon. The thumb URL is
              derived from image_url with the .thumb.png suffix the
              backend writes on upload. Wrapped in the same dark
              glass backdrop the player end uses, so the admin sees
              the actual on-platform appearance even in the list. */}
          {o.image_url ? (
            <div className="relative w-10 h-10 rounded-xl overflow-hidden
                            bg-gradient-to-br from-[#0a1626] via-[#0e1d33] to-[#1a0f08]
                            ring-1 ring-inset ring-white/10 grid place-items-center shrink-0">
              {/* Phase 16.5: cyan/brand halos removed; small black
                  drop-shadow scaled to the 40px thumb. */}
              <img
                src={o.image_url.replace(/\.png$/, '.thumb.png')}
                alt=""
                loading="lazy"
                decoding="async"
                className="relative w-[78%] h-[78%] object-contain
                           drop-shadow-[0_2px_3px_rgba(0,0,0,0.20)]"
              />
            </div>
          ) : (
            <span className="grid place-items-center w-10 h-10 rounded-xl
                             bg-violet-50 text-violet-700
                             dark:bg-violet-400/15 dark:text-violet-300 shrink-0">
              <Icon name="card_giftcard" size={18} filled />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-arena-900 dark:text-white truncate">
              {o.title}
            </div>
            <div className="text-[11px] text-arena-600 dark:text-neon-200/60 truncate">
              {TYPE_LABEL[o.offer_type] || o.offer_type} · #{o.id}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${STATUS_PILL[o.status]}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {o.status}
              </span>
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ring-1 ring-inset
                ${o.approval === 'auto'
                  ? 'bg-cyan-50 text-cyan-700 ring-cyan-200/60 dark:bg-cyan-400/10 dark:text-cyan-300 dark:ring-cyan-400/30'
                  : 'bg-amber-50 text-amber-700 ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30'}`}>
                {o.approval}
              </span>
            </div>
          </div>
          <Icon name="chevron_right" size={16} className="text-arena-400 dark:text-neon-200/40 shrink-0" />
        </div>

        {o.description && (
          <p className="text-[12px] text-arena-700 dark:text-neon-200/75 line-clamp-2 mb-3">
            {o.description}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 border-t border-arena-200/40 dark:border-arena-700/40 pt-3">
          <div className="min-w-0">
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50 uppercase tracking-wider font-bold">Reward</div>
            <div className="text-[14px] font-display font-extrabold text-violet-700 dark:text-violet-300 tabular-nums truncate">{reward}</div>
          </div>
          {budgetText && (
            <div className="min-w-0">
              <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50 uppercase tracking-wider font-bold">Budget</div>
              <div className="text-[12px] font-semibold text-arena-900 dark:text-white tabular-nums truncate">{budgetText}</div>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
